import { useGetPondNameById } from 'modules/pond/api/selector'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import LoadingButton from '@mui/lab/LoadingButton'
import { IconButton } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog, { DialogProps } from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { SxProps, Theme } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

interface BaseRecordDialogProps {
  onClose: () => void
  open: boolean
  title: string
  isSubmitting: boolean
  formId: string
  submitButtonLabel?: string
  submitButtonSx?: SxProps<Theme>
  hideActions?: boolean
  disabledSubmit?: boolean
  isShowPondName?: boolean
  enableCloseButton?: boolean
  maxWidth?: DialogProps['maxWidth']
  fullWidth?: boolean
}

const BaseRecordDialog: FC<BaseRecordDialogProps> = ({
  children,
  onClose,
  open,
  title,
  isSubmitting,
  formId,
  submitButtonLabel,
  submitButtonSx,
  hideActions = false,
  disabledSubmit = false, // default value
  isShowPondName = true,
  enableCloseButton = false,
  maxWidth = 'sm',
  fullWidth = false,
}) => {
  const { t } = useTranslation(['common', 'pond'])
  const { pondId } = useParams()
  const { data: pondInfo } = useGetPondNameById(pondId || '')

  return (
    <Dialog open={open} onClose={onClose} disableRestoreFocus maxWidth={maxWidth} fullWidth={fullWidth}>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              {title}
            </Typography>
            {isShowPondName && (
              <Typography variant="h6" fontWeight="bold" color="primary">
                {`${t('pond:pond')} ${pondInfo?.name}`}
              </Typography>
            )}
          </Stack>

          {enableCloseButton && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                color: (theme) => theme.palette.grey[400],
              }}
            >
              <CloseRoundedIcon fontSize="medium" />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>{children}</DialogContent>
      {!hideActions && (
        <DialogActions>
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button variant="outlined" color="inherit" onClick={onClose}>
                {t('common:cancel')}
              </Button>

              <LoadingButton
                variant="contained"
                type="submit"
                form={formId}
                loading={isSubmitting}
                disabled={disabledSubmit}
                sx={submitButtonSx}
              >
                {submitButtonLabel || t('common:save')}
              </LoadingButton>
            </Stack>
          </Box>
        </DialogActions>
      )}
     
    </Dialog>
  )
}

export default BaseRecordDialog
